"use client";
import { useCollectionContext } from "./collection-context.js";
import { createElement, createHook, forwardRef, useId, useMergeRefs } from "@ariakit/react-utils";
import { identity, removeUndefinedValues } from "@ariakit/utils";
import { useEffect, useRef } from "react";
//#region src/collection/collection-item.tsx
const TagName = "div";
/**
* Returns props to create a `CollectionItem` component. This hook will register
* the item in the collection store. If this hook is used in a component that is
* wrapped by `Collection` or a component that implements `useCollection`,
* there's no need to explicitly pass the `store` prop.
* @see https://ariakit.com/components/collection
* @example
* ```jsx
* const store = useCollectionStore();
* const props = useCollectionItem({ store });
* <Role {...props}>Item</Role>
* ```
*/
const useCollectionItem = createHook(function useCollectionItem({ store, shouldRegisterItem = true, getItem = identity, element, ...props }) {
	const context = useCollectionContext();
	store = store || context;
	const id = useId(props.id);
	const ref = useRef(element);
	useEffect(() => {
		const element = ref.current;
		if (!id) return;
		if (!element) return;
		if (!shouldRegisterItem) return;
		const item = getItem({
			id,
			element
		});
		return store?.renderItem(item);
	}, [
		id,
		shouldRegisterItem,
		getItem,
		store
	]);
	props = {
		...props,
		ref: useMergeRefs(ref, props.ref)
	};
	return removeUndefinedValues(props);
});
/**
* Renders an item in a collection. The collection store can be passed
* explicitly through the
* [`store`](https://ariakit.com/reference/collection-item#store) prop or
* implicitly through the parent
* [`Collection`](https://ariakit.com/reference/collection) component.
* @see https://ariakit.com/components/collection
* @example
* ```jsx
* const store = useCollectionStore();
* <CollectionItem store={store}>Item 1</CollectionItem>
* <CollectionItem store={store}>Item 2</CollectionItem>
* <CollectionItem store={store}>Item 3</CollectionItem>
* ```
*/
const CollectionItem = forwardRef(function CollectionItem(props) {
	return createElement(TagName, useCollectionItem(props));
});
//#endregion
export { CollectionItem, useCollectionItem };

//# sourceMappingURL=collection-item.js.map